'use client';

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { track } from '@vercel/analytics';
import { X } from 'lucide-react';

/**
 * Google Calendar appointment schedule (ASK AI sales calendar). The ?gv=true
 * variant is the embeddable view, the same one Google's official
 * scheduling-button script opens in its popup, so we host it inside our own
 * branded modal instead of injecting Google's grey button + external script.
 */
const GOOGLE_APPOINTMENTS_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ3dvAQh3IvN55HSnXeO_aunx52G9VAcmM0biddA4whPNmUi4OyUoLfpFFHtUnxcKYKNS7W3FAg7?gv=true';

export function SchedulingModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Conversion signal: fires once per open, from every CTA that launches the
  // scheduler (hero, about, contact section, header, CTA band).
  useEffect(() => {
    if (open) track('marketing_scheduling_opened');
  }, [open]);

  if (!open) return null;

  // Pin the booking page to the visitor's own timezone (the modal only ever
  // renders client-side, so Intl reflects the visitor's device).
  const bookingUrl = `${GOOGLE_APPOINTMENTS_URL}&ctz=${encodeURIComponent(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-[900px] h-[85vh] max-h-[800px] bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
        <iframe
          src={bookingUrl}
          className="w-full h-full border-0"
          title="Book an appointment"
        />
      </div>
    </div>,
    document.body,
  );
}

export function SchedulingEmbed() {
  return (
    <div className="w-full max-w-[900px] mx-auto">
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
        <iframe
          src={GOOGLE_APPOINTMENTS_URL}
          className="w-full h-[500px] md:h-[600px] border-0"
          title="Book an appointment"
        />
      </div>
    </div>
  );
}

/** Back-compat aliases for pre-rename imports. */
export {
  SchedulingModal as NotionCalendarModal,
  SchedulingEmbed as NotionCalendarEmbed,
};
